'use client';

import { c as _c } from "react/compiler-runtime";
import { produce } from '@/globals/helpers/immer';
import { useSearchParams } from 'next/navigation';
import { PropsWithChildren, createContext, useCallback, useContext, useMemo, useOptimistic } from 'react';
import { useDOPRouter } from '@/globals/routing/DOPRouter';
import { pushInternalLinkEvent } from '@/globals/analytics/events/pushLinkEvent';
import { SelectedFiltersMap, getSelectedFilters } from './getSelectedFilters';
type OptimisticSelectedFilters = ReturnType<typeof useOptimistic<SelectedFiltersMap, Parameters<typeof getUpdatedSelectedFilters>[1]>>;
export const FilterStateContext = createContext<OptimisticSelectedFilters | undefined>(undefined);
const useGetSelectedFilters = (groupKeys, subjectKey) => {
  const $ = _c(4);
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== groupKeys || $[1] !== searchParams || $[2] !== subjectKey) {
    t0 = getSelectedFilters(groupKeys, subjectKey, searchParams);
    $[0] = groupKeys;
    $[1] = searchParams;
    $[2] = subjectKey;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
const getUpdatedSelectedFilters = produce((selectedFiltersDraft: SelectedFiltersMap, {
  groupKey,
  optionKey,
  checked
}: {
  groupKey?: string;
  optionKey?: string;
  checked: boolean;
}) => {
  if (groupKey == null) {
    if (!checked) selectedFiltersDraft.clear();
    return;
  }
  const filterGroup = selectedFiltersDraft.get(groupKey);
  if (filterGroup == null) return;
  if (optionKey == null) {
    if (!checked) filterGroup.clear();
    return;
  }
  if (checked) {
    filterGroup.add(optionKey);
  } else {
    filterGroup.delete(optionKey);
  }
});
export const SelectedFiltersProvider = t0 => {
  const $ = _c(10);
  const {
    children,
    groupKeys,
    subjectKey
  } = t0;
  const selectedFilters = useGetSelectedFilters(groupKeys, subjectKey);
  const router = useDOPRouter();
  const [optimisticSelectedFilters, updateOptimisticSelectedFilters] = useOptimistic(selectedFilters, _temp);
  let t1;
  if ($[0] !== optimisticSelectedFilters || $[1] !== router || $[2] !== updateOptimisticSelectedFilters) {
    t1 = t2 => {
      const {
        groupKey,
        optionKey,
        checked
      } = t2;
      const newSelectedFilters_0 = getUpdatedSelectedFilters(optimisticSelectedFilters, {
        groupKey,
        optionKey,
        checked
      });
      updateOptimisticSelectedFilters(newSelectedFilters_0);
      const searchParams = new URLSearchParams();
      for (const [key, values] of newSelectedFilters_0.entries()) {
        for (const value of values) {
          searchParams.append(key, value);
        }
      }
      router.replace(`?${searchParams.toString()}`, {
        scroll: false
      });
      pushInternalLinkEvent({
        href: window.location.pathname,
        linkType: "filterChanged"
      });
    };
    $[0] = optimisticSelectedFilters;
    $[1] = router;
    $[2] = updateOptimisticSelectedFilters;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const navigateFilters = t1;
  let t2;
  if ($[4] !== navigateFilters || $[5] !== optimisticSelectedFilters) {
    t2 = [optimisticSelectedFilters, navigateFilters];
    $[4] = navigateFilters;
    $[5] = optimisticSelectedFilters;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== t2) {
    t3 = <FilterStateContext.Provider value={t2}>{children}</FilterStateContext.Provider>;
    $[7] = children;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
export const useSelectedFilters = () => {
  const value = useContext(FilterStateContext);
  if (value == null) {
    throw new Error("useSelectedFilters must be used within a SelectedFiltersProvider");
  }
  return value;
};
export const useCountSelectedFilters = () => {
  const $ = _c(3);
  const [selectedFilters] = useSelectedFilters();
  let count = 0;
  if ($[0] !== count || $[1] !== selectedFilters) {
    for (const selectedFilter of selectedFilters.values()) {
      count = count + selectedFilter.size;
    }
    $[0] = count;
    $[1] = selectedFilters;
    $[2] = count;
  } else {
    count = $[2];
  }
  return count;
};
function _temp(_, newSelectedFilters) {
  return newSelectedFilters;
}